import { useCallback, useMemo } from 'react';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { getGrantedConsentPreferences } from '@/lib/adapters';
import { pushDataLayerEvent, type DataLayerEvent, type DataLayerEventName } from '@/lib/analytics/data-layer';
import { applySiteTrackingPolicy } from '@/lib/analytics/site-tracking-policy';

/**
 * Consent-aware dataLayer API. Components provide only event data; the hook
 * owns consent gating and common site/currency context.
 */
export function useDataLayer() {
    const { site, language, currency } = useSite();
    const consentPreferences = getGrantedConsentPreferences();
    const canTrack = consentPreferences.length > 0;

    const track = useCallback(
        <TName extends DataLayerEventName>(event: DataLayerEvent<TName>): void => {
            if (!canTrack) {
                return;
            }
            const scopedEvent = applySiteTrackingPolicy(event as DataLayerEvent, site.id);
            if (scopedEvent) {
                void pushDataLayerEvent(scopedEvent);
            }
        },
        [canTrack, site.id]
    );

    return useMemo(
        () => ({
            track,
            canTrack,
            siteId: site.id,
            affiliation: site.id,
            localeId: language,
            language,
            currency,
        }),
        [canTrack, currency, language, site.id, track]
    );
}
